import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { findDOMNode } from 'react-dom';
import { renderToString } from 'react-dom/server';
import { CancelToken } from 'axios';
import { List, fromJS } from 'immutable';

import {PopupContainer, RealtyItem, LoaderItem} from '../../Maps/Controls/Popup';
import Announcement from '../../../classes/announcement/announcement';
import GeoObject from '../../../classes/geo/geoObject';
import { getKeys, getValues } from '../../../utils/objectPolyfill';
import api from '../../../apiSingleton';
import { isTouchDevice } from '../../../utils/browser';
import { polygonFromString, polygonToString, boundsFromString } from '../../../utils/polygon';

import { TILE_VENDORS as TILE, VIEW_MODE, MAP_DRAW_MODE as MODE } from '../../../consts';

class MapView extends Component {
  constructor (props) {
    super(props);

    this.filterTimer = null;
    this.requestTimer = null;
    this.source = null;

    this.zoom = 12;
    this.lat = props.domain.get('lat');
    this.lon = props.domain.get('lon');
    this.initAreaPolygon = polygonFromString(props.areaPolygon);  // полигон из урл
    this.initBounds = this.initAreaPolygon.length ? this.initAreaPolygon : boundsFromString(props.bounds);

    this.state = {
      drawMode: MODE.NONE,
      isDrawPolygon: this.initAreaPolygon.length > 0
    };

    this.map = null;              // карта
    this.freeDrawLayer = null;    // слой с полигонами
    this.popup = null;
    this.cluster = null;
    this.tileLayerDGis = null;
    this.tileLayerOSM = null;
    this.thisViewDOM = null;
    this.mapsRootDOM = null;
    this.markerList = {};
    this.pointList = props.points || new List();

    this.loader = renderToString(
      <PopupContainer
        items={[{}]}
        itemElement={LoaderItem}
      />);

    this.handlerResizeWindow = this.handlerResizeWindow.bind(this);
    this.handlerOnLoadMap = this.handlerOnLoadMap.bind(this);
    this.handleChangePolygon = this.handleChangePolygon.bind(this);
    this.handlerCancelPolygon = this.handlerCancelPolygon.bind(this);
    this.handlerOnChangeMap = this.handlerOnChangeMap.bind(this);
    this.renderPoints = this.renderPoints.bind(this);

    this.onDrawPolygon = this.onDrawPolygon.bind(this);
    this.onCancelPolygon = this.onCancelPolygon.bind(this);
    this.onRemovePolygon = this.onRemovePolygon.bind(this);
  }

  componentDidUpdate (prevProps, prevState, snapshot) {
    if (this.props.addressHash !== prevProps.addressHash) {
      this.map && this.map.fitBounds(boundsFromString(this.props.bounds));

      const polygon = polygonFromString(this.props.areaPolygon);
      if (polygon.length) {
        this.onRemovePolygon();
      }
    }
  }

  initMap () {
    // создать карту
    const L = window.L;
    this.map = L.map(this.thisViewDOM, {
      zoomControl: false,
      maxZoom: 18,
      minZoom: 9
    });

    this.cluster = window.L.markerClusterGroup({
      spiderfyOnMaxZoom: false,
      zoomToBoundsOnClick: false,
      showCoverageOnHover: false,
      maxClusterRadius: 50
    });
    this.map.addLayer(this.cluster);

    // обработчики на изменения
    this.map.on('load', this.handlerOnLoadMap)
      .setView([this.lat, this.lon], this.zoom);
    this.map.on('moveend', this.handlerOnChangeMap);
    this.map.on('zoomstart', () => this.map.closePopup());

    this.cluster.on('clusterclick', (e) => {
      const currentZoom = this.map.getZoom();
      if (currentZoom < 16) {
        this.map.setView(e.layer.getLatLng(), currentZoom + (16 - currentZoom <= 2 ? 1 : 2));
      } else {
        const items = e.layer.getAllChildMarkers()
          .map(item => item.options.data);

        this.openBalloon(e.layer.getLatLng(), items);
      }
    });

    this.cluster.on('click', (e) => {
      const items = [e.layer.options.data];
      this.openBalloon(e.layer.getLatLng(), items);
    });

    // спозицировать если есть область
    if (this.initBounds) {
      this.map.fitBounds(this.initBounds);
    }

    // добавить контролы
    this.map.addControl(new L.Control.Fullscreen({
      position: 'topright',
      title: {
        'false': 'Включить полноэкранный режим',
        'true': 'Выйти из полноэкранного режима'
      }
    }));

    L.control.zoom({
      position: 'topright',
      zoomInTitle: 'Увеличить',
      zoomOutTitle: 'Уменьшить'
    }).addTo(this.map);

    // добавить fallback для слоев
    this.tileLayerDGis = L.tileLayer(TILE.dgis.url, {attribution: TILE.dgis.attribution, subdomains: '0123'});
    this.tileLayerDGis.addTo(this.map);

    this.tileLayerOSM = L.tileLayer(TILE.osm.url, {attribution: TILE.osm.attribution});

    this.tileLayerDGis.on('tileerror', () => this.map.addLayer(this.tileLayerOSM));
    this.tileLayerDGis.on('tileload', () => this.map.removeLayer(this.tileLayerOSM));

    if (!isTouchDevice()) {
      // freeDraw - выделить область
      this.freeDrawLayer = new window.FreeDraw({
        maximumPolygons: 1,
        mode: MODE.NONE,
        leaveModeAfterCreate: true
      });

      this.freeDrawLayer.on('markers', this.handleChangePolygon);
      this.freeDrawLayer.onAdd(this.map);

      if (this.initAreaPolygon.length) {
        this.freeDrawLayer.create(this.initAreaPolygon);
        this.freeDrawLayer.mode(MODE.EDIT);
      }
    }
  }

  openBalloon (coords, items) {
    if (!this.popup) {
      this.popup = window.L.popup({
        autoPanPadding: [25, 85],
        closeButton: false
      });
    }

    this.popup.setLatLng(coords).setContent(this.loader);
    this.map.openPopup(this.popup);

    this.source && this.source.cancel('click balloon...');
    clearTimeout(this.requestTimer);

    this.requestTimer = setTimeout(() => {
      this.source = CancelToken.source();
      const isDealCompleted = this.props.isDealCompleted;

      api.announcement
        .fetchAnnouncementList(this.props.dealType, this.props.realtyType, {idList: items, isDealCompleted}, this.source)
        .then(data => {
          const items = data.items.map(item => {
            const data = new Announcement(fromJS(item), this.props.dictionary);
            return GeoObject.makePopupItem(data);
          });

          const content = renderToString(<PopupContainer items={items} itemElement={RealtyItem} />);
          this.popup.setContent(content);
        }).catch(function (thrown) {
          console.warn('Request canceled: ', thrown.message);
        });
    }, 300);
  }

  renderPoints () {
    if (!this.map) {
      return;
    }

    const mainIcon = window.L.divIcon({className: 'maps-placemark maps-placemark-blue', iconAnchor: [10, 10]});

    const oldMarkers = {};
    const newMarkers = {};    // новые маркеры
    const commonMarkers = {}; // общие маркеры

    this.pointList.map(item => {
      const id = item.get('id');
      if (this.markerList[id]) {
        commonMarkers[id] = true;
      } else {
        newMarkers[item.get('id')] = window.L.marker(
          [item.get('lat'), item.get('lon')],
          {icon: mainIcon, data: id}
        );
      }
    });

    const currentIdList = getKeys(this.markerList);
    currentIdList.map(id => {
      if (!commonMarkers[id]) {
        oldMarkers[id] = this.markerList[id];
      }
    });

    const addLayers = getValues(newMarkers);
    const removeLayers = getValues(oldMarkers);

    addLayers.length && this.cluster.addLayers(addLayers);
    removeLayers.length && this.cluster.removeLayers(removeLayers);

    for (let id in oldMarkers) {
      delete this.markerList[id];
    }

    for (let id in newMarkers) {
      this.markerList[id] = newMarkers[id];
    }
  }

  getBounds () {
    const bounds = this.map.getBounds();

    return [
      bounds.getSouthWest().lng, bounds.getSouthWest().lat,
      bounds.getNorthEast().lng, bounds.getNorthEast().lat
    ].join(',');
  }

  handlerOnLoadMap () {
    this.renderPoints();

    const filter = {
      viewMode: VIEW_MODE.map
    };

    if (!this.initBounds) {
      filter.bounds = this.getBounds();
    }

    this.props.onUpdateFilter(filter, true);
  }

  /**
   * Событие при изменении полигона
   * @param e
   */
  handleChangePolygon (e) {
    if (!e.latLngs.length) {
      return;
    }

    const polygon = e.latLngs[0].map(point =>
        [ Math.round(point.lat * 10000) / 10000, Math.round(point.lng * 10000) / 10000 ]
    );

    const areaPolygon = polygonToString(polygon);

    this.props.onUpdateFilter({areaPolygon});
    this.map.fitBounds(polygon);
  }

  handlerCancelPolygon (e) {
    if (this.freeDrawLayer && e.key === 'Escape') {
      this.freeDrawLayer.cancel();
    }
  }

  handlerOnChangeMap () {
    clearTimeout(this.filterTimer);

    this.filterTimer = setTimeout(() => {
      const filter = { viewMode: VIEW_MODE.map };
      const polygon = polygonFromString(this.props.areaPolygon);

      if (!polygon.length) {
        filter.bounds = this.getBounds();
      }

      this.props.onUpdateFilter(filter);
    }, 300);
  }

  handlerResizeWindow () {
    this.mapsRootDOM.style.height = (window.innerHeight - 162) + 'px';
  }

  componentDidMount () {
    this.thisViewDOM = findDOMNode(this);
    this.mapsRootDOM = document.getElementById('map-result');

    window.addEventListener('resize', this.handlerResizeWindow);
    window.addEventListener('keydown', this.handlerCancelPolygon);

    this.handlerResizeWindow();

    this.initMap();
  }

  componentWillUnmount () {
    if (this.freeDrawLayer) {
      this.freeDrawLayer.clear();
      this.map.removeLayer(this.freeDrawLayer);
    }

    window.removeEventListener('resize', this.handlerResizeWindow);
    window.removeEventListener('keydown', this.handlerCancelPolygon);

    this.mapsRootDOM.style.height = 'initial';
    this.map.remove();
  }

  componentWillReceiveProps (nextProps, nextContext) {
    if (!this.pointList.equals(nextProps.points)) {
      this.pointList = nextProps.points;

      this.renderPoints();
    }
  }

  onDrawPolygon () {
    this.freeDrawLayer.mode(MODE.CREATE | MODE.EDIT);

    this.setState({
      isDrawPolygon: true,
      drawMode: MODE.CREATE | MODE.EDIT
    });
    this.props.onUpdateFilter({areaPolygon: null});
  }

  onCancelPolygon () {
    this.freeDrawLayer.mode(MODE.NONE);

    this.setState({
      isDrawPolygon: false,
      drawMode: MODE.NONE
    });
    this.props.onUpdateFilter({
      areaPolygon: null,
      bounds: this.getBounds()
    });
  }

  onRemovePolygon () {
    this.freeDrawLayer.clear();
    this.freeDrawLayer.mode(MODE.NONE);

    this.setState({
      isDrawPolygon: false,
      drawMode: MODE.NONE
    });
    this.props.onUpdateFilter({
      areaPolygon: null,
      bounds: this.getBounds()
    });
  }

  renderDrawBtn () {
    if (isTouchDevice()) {
      return null;
    }

    const pencil = (
      <button type='button' className='btn btn-base' onClick={this.onDrawPolygon}>
        <i className='icon icon-pencil' /> ВЫДЕЛИТЬ ОБЛАСТЬ
      </button>
    );

    const clear = (
      <button type='button' className='btn btn-base' onClick={this.onRemovePolygon}>
        <i className='icon icon-trash' /> ОЧИСТИТЬ ОБЛАСТЬ
      </button>
    );

    const cancel = (
      <button type='button' className='btn btn-base' onClick={this.onCancelPolygon}>
        <i className='icon icon-close' /> ОТМЕНИТЬ ВЫДЕЛЕНИЕ
      </button>
    );

    const polygon = polygonFromString(this.props.areaPolygon);
    const isDraw = this.state.isDrawPolygon;

    return (
      <div className='map-draw-panel'>
        { !polygon.length && !isDraw && pencil}
        { !polygon.length && isDraw && cancel}
        { polygon.length > 4 && isDraw && clear}
      </div>
    );
  }

  render () {
    return (
      <React.Fragment>
        <div className='map-view-content' />
        {this.renderDrawBtn()}
      </React.Fragment>
    );
  }
}

MapView.propTypes = {
  addressHash: PropTypes.string,
  bounds: PropTypes.string,
  areaPolygon: PropTypes.string,

  domain: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,

  dealType: PropTypes.string.isRequired,
  realtyType: PropTypes.string.isRequired,
  points: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
  dictionary: PropTypes.object.isRequired,

  isDealCompleted: PropTypes.bool,
  onUpdateFilter: PropTypes.func
};

MapView.defaultProps = {
  isDealCompleted: false,
  bounds: null,
  addressHash: '',
  onUpdateFilter: () => null
};

export default MapView;



// WEBPACK FOOTER //
// ./src/ui/Views/Announcements/MapView.jsx